<script setup lang="ts">
/**
 * ElectionCard Component
 *
 * Displays a single election contract with candidate comparison.
 * Always renders all candidates as horizontally scrollable compact tiles with a visible scrollbar.
 *
 * Receives a single `candidates: CandidateTableRow[]` prop.
 * Bid-ask prices are resolved via `bidAskMap` passed from the parent (ElectionGrid).
 */
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import CandidateTile from './CandidateTile.vue';
import { PARTY_COLORS, type ElectionContract, type PartyCode, type CandidateTableRow } from '../types/election.types';
import type { PositionData } from '@/types/accountPnl.types';
import type { SelectionInfo } from '../composables/useElectionData';
import { formatCompactNumber } from '@/utils/formatNumber';
import { useOrderTicket } from '../composables/useOrderTicket';
import { allYesConids, allNoConids } from '../utils/matchedConids';

// Props
const props = withDefaults(
  defineProps<{
    /** Election contract data */
    contract: ElectionContract;
    /** All candidates for this race */
    candidates: CandidateTableRow[];
    /** Election type label */
    electionType?: 'Governor' | 'House' | 'Senate';
    /** Index for House districts */
    index?: number;
    /** Bid-ask data map keyed by conid */
    bidAskMap?: Record<number, { bid: number | null; ask: number | null }>;
    /** Position data map keyed by conid */
    positionsMap?: Record<number, PositionData>;
    /** Whether to show the position footer (grid-wide toggle) */
    showFooter?: boolean;
    /** Whether this card is visible in the viewport (controls data fetching) */
    visible?: boolean;
  }>(),
  {
    showFooter: true,
    visible: true,
  },
);

// Emits
const { t } = useI18n();

const emit = defineEmits<{
  /** Emitted when card is clicked to view details */
  select: [info: SelectionInfo];
  /** Emitted when a specific candidate is clicked to view their details */
  selectCandidate: [info: SelectionInfo];
  /** Emitted when the set of visible candidate conids changes (scroll-level lazy loading) */
  'update:visibleConids': [conids: Set<number>];
}>();

const { openOrderTicket } = useOrderTicket();

// "Once visible, stay rendered" — latch to true on first visibility
const everVisible = ref(props.visible);
watch(
  () => props.visible,
  (v) => {
    if (v) everVisible.value = true;
  },
);

const realOpenInterest = computed(() => props.contract.openInterest);

// Type badge label - uses state code from contract name
const typeLabel = computed(() => props.contract.stateCode);

// Badge color based on leading party — yellow when API omits party (toss-up)
const badgeColor = computed(() => {
  const { favorite, price } = props.contract;
  if (!favorite) return PARTY_COLORS.TOSS_UP;

  if (favorite === 'GOP') {
    if (price > 80) return PARTY_COLORS.GOP_DARK;
    if (price > 65) return PARTY_COLORS.GOP_MID;
    return PARTY_COLORS.GOP_LIGHT;
  } else if (favorite === 'GRN') {
    if (price > 80) return PARTY_COLORS.GRN_DARK;
    if (price > 65) return PARTY_COLORS.GRN_MID;
    return PARTY_COLORS.GRN_LIGHT;
  } else {
    if (price > 80) return PARTY_COLORS.DEM_DARK;
    if (price > 65) return PARTY_COLORS.DEM_MID;
    return PARTY_COLORS.DEM_LIGHT;
  }
});

// --- Candidate data ---

// Display candidates with resolved bid prices, ordered by leading candidate pinning
const displayCandidates = computed(() => {
  if (!props.candidates?.length) return [];

  const candidates = props.candidates.map((c) => {
    const bidAsk = props.bidAskMap?.[c.conid_yes];
    const price = bidAsk?.bid != null && bidAsk.bid >= 0 ? Math.round(bidAsk.bid * 1000) / 10 : c.price;
    return { ...c, displayPrice: price };
  });

  const leadingDem = props.contract.leadingDemConid;
  const leadingRep = props.contract.leadingRepConid;

  // When no filters applied: backend sends leading conids — pin them to front
  if (leadingDem || leadingRep) {
    const leading: typeof candidates = [];
    const rest: typeof candidates = [];

    for (const c of candidates) {
      if (c.conid_yes === leadingDem || c.conid_yes === leadingRep) {
        leading.push(c);
      } else {
        rest.push(c);
      }
    }
    return [...leading, ...rest];
  }

  // When filter applied: no leading fields, use API response order as-is
  return candidates;
});

// Stable identity key — only changes when candidate list structure changes,
// NOT when prices update. Derives from props.candidates (no bidAskMap dependency)
// to prevent reactive loops: bidAskMap → displayCandidates → emit conids → bidAskMap.
const candidateIdentityKey = computed(() => props.candidates.map((c) => c.conid_yes).join(','));

// Whether this card is a head-to-head (exactly 2 candidates)
const isHeadToHead = computed(() => displayCandidates.value.length === 2);

// Whether this card has a single candidate (show centered)
const isSingleCandidate = computed(() => displayCandidates.value.length === 1);

// Party-to-text-class mapping (used by position footer)
function partyTextClass(party: PartyCode): string {
  if (party === 'DEM') return 'text-democrat';
  if (party === 'GOP') return 'text-republican';
  if (party === 'GRN') return 'text-green';
  return 'text-independent';
}

// --- Lazy loading for candidate tiles within horizontal scroll ---
const scrollTrackRef = ref<HTMLElement | null>(null);
const visibleConids = ref<Set<number>>(new Set());
let tileObserver: IntersectionObserver | null = null;

function setupTileObserver() {
  cleanupTileObserver();
  if (!scrollTrackRef.value) return;

  // Pre-seed with first 3 candidates (cap initial fetch)
  const initial = new Set<number>();
  const candidates = displayCandidates.value;
  for (let i = 0; i < Math.min(candidates.length, 3); i++) {
    if (candidates[i].conid_yes) initial.add(candidates[i].conid_yes);
  }
  visibleConids.value = initial;

  // Create observer for remaining tiles on horizontal scroll
  tileObserver = new IntersectionObserver(
    (entries) => {
      const updated = new Set(visibleConids.value);
      let grew = false;
      entries.forEach((entry) => {
        const conid = Number((entry.target as HTMLElement).dataset.conid);
        if (!Number.isNaN(conid) && entry.isIntersecting && !updated.has(conid)) {
          updated.add(conid);
          grew = true;
        }
      });
      if (grew) {
        visibleConids.value = updated;
      }
    },
    { root: scrollTrackRef.value, rootMargin: '0px 100px 0px 100px' },
  );

  scrollTrackRef.value.querySelectorAll<HTMLElement>('[data-conid]').forEach((el) => {
    tileObserver!.observe(el);
  });
}

function cleanupTileObserver() {
  tileObserver?.disconnect();
  tileObserver = null;
}

onMounted(() => nextTick(setupTileObserver));
onBeforeUnmount(cleanupTileObserver);

// Re-observe when candidate list structure changes or card becomes visible for the first time.
// Skips h2h/single cards (they have no scroll track — handled by separate watcher below).
// Uses flush: 'post' to guarantee DOM is ready after v-if swap (nextTick alone is insufficient).
watch(
  [candidateIdentityKey, everVisible],
  () => {
    if (everVisible.value && !isHeadToHead.value && !isSingleCandidate.value) {
      setupTileObserver();
    }
  },
  { flush: 'post' },
);

// Notify parent of which conids are visible in the scroll track
watch(visibleConids, (conids) => emit('update:visibleConids', conids));

// For h2h cards (1-2 candidates), emit all candidate conids when card becomes visible.
// The tile IntersectionObserver only works for the 3+ candidate scroll track —
// h2h cards have no scrollTrackRef, so we handle them separately.
watch(
  [() => props.visible, () => isHeadToHead.value || isSingleCandidate.value, candidateIdentityKey],
  ([visible, isH2h]) => {
    if (visible && isH2h) {
      const conids = new Set(displayCandidates.value.map((c) => c.conid_yes).filter(Boolean));
      visibleConids.value = conids;
    }
  },
  { immediate: true },
);

function isTileVisible(conid: number): boolean {
  return visibleConids.value.has(conid);
}

// Handlers
function handleCardClick() {
  emit('select', {
    stateCode: props.contract.stateCode,
    type: props.electionType,
    index: props.index,
  });
}

function handleCandidateSelect(candidateConid: number) {
  emit('selectCandidate', {
    stateCode: props.contract.stateCode,
    type: props.electionType,
    index: props.index,
    productConid: props.contract.productConid,
    candidateConid,
  });
}

function handleBuyClick(conid: number) {
  if (conid) openOrderTicket(conid, undefined, 'BEST');
}

function handleCloseClick(conid: number) {
  if (conid) openOrderTicket(conid, 'close', 'BEST');
}

function getCandidateCloseState(candidate: CandidateTableRow): boolean {
  const positions = props.positionsMap;
  if (!positions) return false;
  // A "no" position may be held on a matched exchange, not just the primary contract.
  return allNoConids(candidate).some((conid) => {
    const pos = positions[conid];
    return !!(pos && pos.position !== 0);
  });
}

// --- Position footer ---

interface PrimaryPosition {
  data: PositionData;
  candidate: CandidateTableRow;
  side: 'YES' | 'NO';
}

const primaryPosition = computed<PrimaryPosition | null>(() => {
  const positions = props.positionsMap;
  if (!positions) return null;
  let largest: PrimaryPosition | null = null;
  for (const candidate of props.candidates) {
    // Positions can live on matched exchanges, so scan every yes/no conid.
    for (const [conids, side] of [
      [allYesConids(candidate), 'YES'],
      [allNoConids(candidate), 'NO'],
    ] as const) {
      for (const conid of conids) {
        if (!conid) continue;
        const pos = positions[conid];
        if (pos && (!largest || Math.abs(pos.position) > Math.abs(largest.data.position))) {
          largest = { data: pos, candidate, side };
        }
      }
    }
  }
  return largest;
});

function pnlValueColorClass(pnl: number): string {
  if (pnl > 0) return 'dark:text-(--p-green100) text-(--p-green200)';
  if (pnl < 0) return 'dark:text-(--p-red100) text-(--p-red200)';
  return 'text-muted-color';
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const intFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});
</script>

<template>
  <div
    class="group/card flex h-full w-full flex-col overflow-hidden rounded-2xl border shadow-md transition-shadow hover:shadow-lg"
  >
    <!-- Card Header -->
    <div
      class="flex cursor-pointer items-center justify-between px-6 py-5 transition-colors bg-surface-low hover:bg-surface-container"
      @click="handleCardClick"
    >
      <div class="flex min-w-0 flex-[4] items-center gap-2">
        <div
          class="flex h-8 min-h-8 min-w-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] font-black text-white shadow-sm transition-colors"
          :style="{ backgroundColor: badgeColor }"
        >
          {{ typeLabel }}
        </div>
        <div class="flex min-w-0 items-start gap-2">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <p class="text-on-surface min-w-0 truncate text-sm font-black leading-normal">
                {{ contract.name.substring(0, contract.name.lastIndexOf(' ')) }}
                <template v-if="contract.district"> &middot;{{ contract.district.split(' ')[1] }} </template>
              </p>
            </div>
            <div class="text-muted-color text-xs font-medium uppercase tracking-wide">
              {{ electionType }}
              <template v-if="contract.district"> &middot;{{ contract.district }}</template>
            </div>
          </div>
        </div>
      </div>
      <div class="flex shrink-0 flex-[1] flex-col items-end whitespace-nowrap">
        <div class="text-muted-color flex items-center gap-1 text-xs font-semibold">
          <i class="ibi-chart-pie-sector" />
          {{ formatCompactNumber(realOpenInterest) }} {{ t('oi') }}
        </div>
      </div>
    </div>

    <!-- Full content when card has been visible at least once -->
    <template v-if="everVisible">
      <!-- Candidate Comparison -->
      <div class="election-card-body flex-1 space-y-8 bg-surface p-6">
        <!-- Single or head-to-head (1-2 candidates) -->
        <div v-if="isHeadToHead || isSingleCandidate" class="election-card-h2h">
          <CandidateTile
            :conid="displayCandidates[0].conid_yes"
            :candidate-name="displayCandidates[0].candidateName"
            :party="displayCandidates[0].party"
            :display-price="displayCandidates[0].displayPrice"
            :show-avatar="everVisible"
            :close-state="getCandidateCloseState(displayCandidates[0])"
            @buy="handleBuyClick"
            @close="handleCloseClick"
            @select="handleCandidateSelect"
          />
          <template v-if="isHeadToHead">
            <div class="flex items-center self-center">
              <span class="text-xs font-black uppercase tracking-wider text-muted-color">{{ t('vs') }}</span>
            </div>
            <CandidateTile
              :conid="displayCandidates[1].conid_yes"
              :candidate-name="displayCandidates[1].candidateName"
              :party="displayCandidates[1].party"
              :display-price="displayCandidates[1].displayPrice"
              :show-avatar="everVisible"
              :close-state="getCandidateCloseState(displayCandidates[1])"
              @buy="handleBuyClick"
              @close="handleCloseClick"
              @select="handleCandidateSelect"
            />
          </template>
        </div>

        <!-- Scrollable track for 3+ candidates -->
        <div v-else class="election-card-scroll">
          <div ref="scrollTrackRef" class="election-card-scroll__track">
            <div
              v-for="candidate in displayCandidates"
              :key="candidate.conid_yes"
              class="election-card-scroll__tile flex flex-shrink-0"
              :data-conid="candidate.conid_yes"
            >
              <CandidateTile
                :conid="candidate.conid_yes"
                :candidate-name="candidate.candidateName"
                :party="candidate.party"
                :display-price="candidate.displayPrice"
                :show-avatar="isTileVisible(candidate.conid_yes)"
                :close-state="getCandidateCloseState(candidate)"
                @buy="handleBuyClick"
                @close="handleCloseClick"
                @select="handleCandidateSelect"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Position Footer -->
      <div
        v-if="showFooter"
        class="election-card-footer px-6 py-3"
        :class="primaryPosition ? 'border-t bg-surface-low' : 'bg-surface'"
      >
        <template v-if="primaryPosition">
          <div class="flex items-start justify-between gap-4">
            <!-- Left: position info -->
            <div class="min-w-0 flex-1">
              <!-- Candidate name + side -->
              <div
                class="truncate text-xs font-bold uppercase tracking-wide"
                :class="partyTextClass(primaryPosition.candidate.party)"
              >
                {{ primaryPosition.candidate.candidateName }} - {{ t('win') }}: {{ primaryPosition.side }}
              </div>
              <!-- Stats columns — each key stacked above its value -->
              <div class="mt-1.5 flex items-start gap-4">
                <div>
                  <div class="text-[10px] font-bold uppercase tracking-wider text-muted-color">{{ t('position') }}</div>
                  <div class="mt-0.5 text-xs font-bold font-black">
                    {{ intFormatter.format(primaryPosition.data.position) }}
                  </div>
                </div>
                <div>
                  <div class="text-[10px] font-bold uppercase tracking-wider text-muted-color">{{ t('value') }}</div>
                  <div class="mt-0.5 text-xs font-bold font-black">
                    {{ currencyFormatter.format(primaryPosition.data.marketValue) }}
                  </div>
                </div>
                <div>
                  <div class="text-[10px] font-bold uppercase tracking-wider text-muted-color">{{ t('pnl') }}</div>
                  <div class="mt-0.5 text-xs font-bold" :class="pnlValueColorClass(primaryPosition.data.unrealizedPnl)">
                    {{ primaryPosition.data.unrealizedPnl > 0 ? '+' : ''
                    }}{{ currencyFormatter.format(primaryPosition.data.unrealizedPnl) }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Right: action buttons stacked -->
            <div class="flex flex-col items-end gap-1.5 pt-0.5">
              <button
                class="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-muted-color hover:text-primary-emphasis"
                @click.stop="handleCardClick"
              >
                {{ t('viewAll') }}
              </button>
              <button
                class="flex-1 whitespace-nowrap rounded-lg bg-accent text-on-accent px-1.5 py-1 text-xs font-bold text-center cursor-pointer active:scale-[0.97] hover:opacity-90 transition-all"
                @click.stop="handleCloseClick(primaryPosition!.data.conid)"
              >
                <i class="ibi-close-x-filled mr-0.5 text-[10px] align-[-.1em]" />{{ t('close') }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- Skeleton shimmer when card has never been visible -->
    <template v-else>
      <div class="election-card-body flex-1 bg-surface p-6" aria-busy="true" role="status">
        <div class="election-card__skeleton-tiles">
          <div v-for="n in Math.min(candidates.length || 2, 3)" :key="n" class="election-card__skeleton-tile">
            <div class="election-card__skeleton-avatar animate-pulse" />
            <div class="election-card__skeleton-name animate-pulse" />
            <div class="election-card__skeleton-price animate-pulse" />
          </div>
        </div>
      </div>
      <div v-if="showFooter" class="election-card-footer px-6 py-3 bg-surface" />
    </template>
  </div>
</template>

<style scoped>
/* Consistent card body height across Top Two / All modes */
.election-card-body {
  min-height: 10rem;
}

/* All-candidates horizontal scroll — matches top-two card dimensions */
.election-card-scroll__track {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  scroll-behavior: smooth;

  /* Top padding prevents ring-offset clipping (overflow-x:auto forces overflow-y:auto) */
  padding-top: 1rem;
  padding-bottom: 1rem;

  /* Firefox: thin subtle scrollbar using semantic tokens */
  scrollbar-width: thin;
  scrollbar-color: var(--p-content-border-color) transparent;
}

/* Webkit: hide scrollbar arrow buttons, keep thin track + thumb */
.election-card-scroll__track::-webkit-scrollbar {
  height: 6px;
}

.election-card-scroll__track::-webkit-scrollbar-button {
  display: none;
}

.election-card-scroll__track::-webkit-scrollbar-track {
  background: transparent;
}

.election-card-scroll__track::-webkit-scrollbar-thumb {
  background: var(--p-content-border-color);
  border-radius: 3px;
}

/* Each tile ~39% width: 2 full tiles + gap, with enough peek of the 3rd
   to show ~10% of its centered avatar (ring included) */
.election-card-scroll__tile {
  min-width: 39%;
}

/* Two-candidate head-to-head layout */
.election-card-h2h {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

/* Position footer — fixed height keeps cards aligned across the grid */
.election-card-footer {
  height: 5rem;
}

/* Skeleton shimmer tiles (matches real tile layout) */
.election-card__skeleton-tiles {
  display: flex;
  gap: 1.5rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
  justify-content: center;
}

.election-card__skeleton-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  min-width: 5rem;
}

.election-card__skeleton-avatar {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 9999px;
  background-color: var(--p-skeleton-background, var(--p-surface-200));
}

.election-card__skeleton-name {
  width: 4.5rem;
  height: 0.75rem;
  border-radius: 0.25rem;
  background-color: var(--p-skeleton-background, var(--p-surface-200));
}

.election-card__skeleton-price {
  width: 3rem;
  height: 1.25rem;
  border-radius: 0.375rem;
  background-color: var(--p-skeleton-background, var(--p-surface-200));
}
</style>
